import "swiper/css";
import "swiper/css/pagination";
import { useEffect, useState } from "react";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import GalleryMedia, { isVideoUrl } from "./GalleryMedia";

export type SliderImage = {
  url: string;
  caption?: string | null;
};

type ImageSliderProps = {
  images: SliderImage[];
  activeIndex: number;
  onChange: (index: number) => void;
  isBackground?: boolean;
  isFullHeight?: boolean;
  alwaysSwiper?: boolean;
  onImageClick?: (index: number) => void;
};

export default function ImageSlider({
  images,
  activeIndex,
  onChange,
  isFullHeight = false,
  alwaysSwiper = false,
  onImageClick,
}: ImageSliderProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  if (!images.length) return null;

  const paginationConfig = {
    clickable: true,
    dynamicBullets: true,
    dynamicMainBullets: 3,
  };

  /* =======================
      DESKTOP → static image
     ======================= */
  if (!isMobile && !alwaysSwiper) {
    return (
      <Swiper
        modules={[Pagination]}
        spaceBetween={5}
        onSlideChange={(swiper: SwiperType) => onChange(swiper.activeIndex)}
        pagination={paginationConfig}
        className="[&_.swiper-pagination-lock]:!display-flex"
      >
        {images.map((img, i) => (
          <SwiperSlide key={i} className="!h-48">
            <div className="h-full mt-2 rounded-[20px] overflow-hidden">
              <div
                className={`w-full h-full ${onImageClick ? "cursor-pointer" : ""}`}
                onClick={(e) => {
                  if (isVideoUrl(img.url)) return;
                  e.preventDefault();
                  e.stopPropagation();
                  onImageClick?.(i);
                }}
              >
                <GalleryMedia
                  active={activeIndex === i}
                  src={img.url}
                  alt="تصویر خبر"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    );
  }

  /* =======================
      MOBILE → Swiper slider (or alwaysSwiper)
     ======================= */
  return (
    <div
      className={`relative w-full select-none ${isFullHeight ? "h-full" : ""}`}
    >
      <Swiper
        modules={[Pagination]}
        watchOverflow={false}
        slidesPerView={1}
        spaceBetween={5}
        initialSlide={activeIndex}
        onSlideChange={(swiper: SwiperType) => onChange(swiper.activeIndex)}
        pagination={images.length > 1 ? paginationConfig : false}
        speed={120}
        className={`overflow-hidden rounded-[20px] relative 
        ${
          isFullHeight
            ? "h-full [&_.swiper-pagination]:!bottom-3 [&_.swiper-pagination]:!z-20"
            : "h-[184px] mt-2"
        }
      `}
      >
        {images.map((img, i) => (
          <SwiperSlide key={i}>
            <div className="h-full w-full">
              <div
                className="h-full w-full cursor-pointer"
                onClick={(e) => {
                  if (isVideoUrl(img.url)) return;
                  e.preventDefault();
                  e.stopPropagation();
                  onImageClick?.(i);
                }}
              >
                <GalleryMedia
                  active={activeIndex === i}
                  src={img.url}
                  alt={img.caption ?? ""}
                  className="h-full w-full aspect-square object-cover rounded-2xl"
                />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
